import React, { useEffect, useState, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { PackagesContext } from "../context/PackagesContext";
import { ThemeContext } from "../context/ThemeContext";
import PackagesCards from "./PackagesCards";
import PopularPackageLinks from "./PopularPackageLinks";
import { motion } from "framer-motion";

// Split using commas, avoiding commas inside parentheses
const splitSafe = (text) => {
  if (!text) return [];
  const regex = /, (?![^()]*\))/g;
  return text.split(regex).map((item) => item.trim());
};

function PackagesDescription() {
  const [searchParams] = useSearchParams();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState(null);

  const destinationParam = searchParams.get("destination");
  const { packages, loading } = useContext(PackagesContext);
  const { theme } = useContext(ThemeContext);

  useEffect(() => {
    if (destinationParam && packages.length > 0) {
      const foundPackage = packages.find((pkg) => pkg.id == destinationParam);
      if (foundPackage) {
        setSelectedPackage(foundPackage);
      } else {
        setError("No package found for the selected destination.");
      }
    }
  }, [destinationParam, packages]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="text-center p-8 text-sm font-sans dark:text-white">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 p-8 text-sm font-sans">
        {error}
      </div>
    );
  }

  if (!selectedPackage) {
    return (
      <div className="text-center p-8 text-sm font-sans dark:text-white">
        No package selected.
      </div>
    );
  }

  return (
    <section
      className={`${theme === "dark" ? "dark" : ""
        } pt-24 pb-12 px-4 max-w-screen-xl mx-auto font-sans text-sm mt-8`}
    >
      {/* Package Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden"
      >
        <div className="flex flex-col md:flex-row gap-6 p-6 md:p-10">
          <div className="flex-1 space-y-4">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">
              {selectedPackage.packageName}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {selectedPackage.description}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700 dark:text-gray-300 text-sm mt-4">
              <p>
                <span className="font-semibold">Package Type:</span>{" "}
                {selectedPackage.country === "Maldives"
                  ? "Couple"
                  : "Friends/Family"}
              </p>
              <p>
                <span className="font-semibold">Country:</span>{" "}
                {selectedPackage.country}
              </p>
              <p>
                <span className="font-semibold">Price:</span> ₹
                {selectedPackage.price} /- Per Person
              </p>
              <p>
                <span className="font-semibold">Minimum Group:</span> 04 Pax
              </p>
            </div>
          </div>
          <img
            src={selectedPackage.image2}
            alt={selectedPackage.packageName}
            className="w-full md:w-64 h-auto object-cover rounded-lg shadow"
          />
        </div>
      </motion.div>

      {/* Inclusions and Exclusions */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {/* Inclusions */}
        <div className="bg-green-100 dark:bg-gray-800 p-6 rounded-xl">
          <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-white">
            Inclusions
          </h3>
          <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300 space-y-1">
            {[...splitSafe(selectedPackage.mealInclusions), ...splitSafe(selectedPackage.accommodationDetails)].map(
              (item, index) => (
                <li
                  key={`inc-${index}`}
                  style={{ textIndent: '-0.75rem', paddingLeft: '20px' }}
                >
                  {item}
                </li>

              )
            )}
          </ul>
        </div>

        {/* Exclusions */}
        <div className="bg-red-100 dark:bg-gray-800 p-6 rounded-xl">
          <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-white">
            Exclusions
          </h3>
          <ul className="list-disc pl-5 text-gray-600 dark:text-gray-300 space-y-1">
            {splitSafe(selectedPackage.exclusions).map((item, index) => (
              <li
                key={`exc-${index}`}
                style={{ textIndent: '-0.75rem', paddingLeft: '20px' }}
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>

      {/* Itinerary */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mt-10 bg-blue-100 dark:bg-gray-900 p-6 rounded-xl"
      >
        <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-white">
          {selectedPackage.country === "UAE" ? "Sightseeing" : "Itinerary"}
        </h3>
        <div className="max-h-96 overflow-y-auto pr-2">
          <p className="whitespace-pre-line text-gray-600 dark:text-gray-300 leading-relaxed">
            {selectedPackage.sightseeingAndTransfers}
          </p>
        </div>
      </motion.div>

      {/* Related Packages */}
      <div className="mt-12">
        <PackagesCards />
        <PopularPackageLinks />
      </div>
    </section>
  );
}

export default PackagesDescription;
